import React, { useMemo, useCallback, useState } from 'react';
import { Typography, IconButton } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { FormattedMessage } from 'react-intl';
import RemoveCircleOutlineOutlinedIcon from '@mui/icons-material/RemoveCircleOutlineOutlined';
import { GroupTypeDetails } from 'graphql/types';
import { SimpleRemoveDialogBox } from 'modules/Settings/SimpleRemoveDialogBox';
import { useMessage, useMessages } from 'modules/intl';
import { GroupSelectClassification } from 'modules/Settings/GroupSelectClassification';
import { useFeatureFlag } from 'modules/common/hooks';
import { EnabledDisabledDropdown } from './EnabledDisabledDropdown';
import { OptedInDropdown } from './OptedInDropdown';
import { CollectorRowSettings } from './hooks';

const useStyles = makeStyles((theme) => ({
  content: {
    display: 'flex',
    margin: theme.spacing(1, 0)
  },
  dropDown: {
    display: 'flex',
    alignItems: 'flex-start',
    marginRight: theme.spacing(1)
  },
  removeButton: {
    alignSelf: 'center'
  },
  secondaryAction: {
    display: 'flex',
    alignContent: 'flex-start',
    flexWrap: 'wrap',
    width: '75%',
    margin: theme.spacing(-1)
  },
  text: {
    display: 'flex',
    marginRight: theme.spacing(1),
    marginTop: theme.spacing(1.25),
    color: theme.palette.text.secondary
  },
  autoComplete: {
    minWidth: theme.spacing(20)
  }
}));
type SettingsRowProps = {
  rowID: string;
  rowIndex: number;
  field: CollectorRowSettings;
  onRemove: (rowIndex: number) => void;
  enabledGroupTypes: GroupTypeDetails[];
  isDesktopCollector: boolean;
};

const alertMessage = [
  'settings.removeDialog.title',
  'settings.removeDialog.Remove',
  'settings.removeDialog.Cancel',
  'settings.removeDialog.dialogText1',
  'settings.removeDialog.dialogText2'
];

export const SettingsRow: React.FC<SettingsRowProps> = ({
  rowID,
  field,
  rowIndex,
  onRemove,
  enabledGroupTypes,
  isDesktopCollector
}: SettingsRowProps) => {
  const classes = useStyles();
  const alertDialogBoxMessages = useMessages(alertMessage);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  const handleDialogOpen = useCallback(() => {
    setRemoveDialogOpen(true);
  }, []);
  const handleDialogClose = useCallback(() => {
    setRemoveDialogOpen(false);
  }, []);
  const handleRemove = useCallback(() => {
    onRemove(rowIndex);
  }, [onRemove, rowIndex]);

  const contextText = useMemo(
    () => [
      alertDialogBoxMessages['settings.removeDialog.dialogText1'],
      alertDialogBoxMessages['settings.removeDialog.dialogText2']
    ],
    [alertDialogBoxMessages]
  );

  const groupSelectClassificationClasses = useMemo(
    () => ({ autoComplete: classes.autoComplete }),
    [classes.autoComplete]
  );
  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');
  const removeButtonMsg = useMessage('settings.remove');

  return (
    <div
      className={classes.content}
      data-testid={`setting-content-layout-${rowIndex}`}
    >
      <div className={classes.dropDown}>
        <EnabledDisabledDropdown
          labelTextId={
            !isDesktopCollector ? 'collectorSetting.collectorState' : undefined
          }
          value={field.enableDisabled}
          rowID={rowID}
        />
        {!isDesktopCollector && (
          <OptedInDropdown value={field.optedIn} rowID={rowID} />
        )}
        <Typography display="inline" className={classes.text} variant="body2">
          <FormattedMessage id="settings.usersIn" />
        </Typography>
      </div>
      <div className={classes.secondaryAction}>
        {enabledGroupTypes &&
          enabledGroupTypes.map(({ value }) => (
            <GroupSelectClassification
              groupType={value}
              key={value}
              rowID={rowID}
              defaultValues={field[value]}
              classes={groupSelectClassificationClasses}
            />
          ))}
      </div>
      <div className={classes.removeButton}>
        <IconButton
          color="secondary"
          onClick={handleDialogOpen}
          data-testid={`remove-button-${rowID}`}
          size="large"
          aria-label={isTiwScreenReaderEnabled ? removeButtonMsg : undefined}
        >
          <RemoveCircleOutlineOutlinedIcon />
        </IconButton>
        <SimpleRemoveDialogBox
          removeDialogOpen={removeDialogOpen}
          handleRemove={handleRemove}
          handleDialogClose={handleDialogClose}
          title={alertDialogBoxMessages['settings.removeDialog.title']}
          contentText={contextText}
          textButton={alertDialogBoxMessages['settings.removeDialog.Remove']}
          containedButton={
            alertDialogBoxMessages['settings.removeDialog.Cancel']
          }
          icon={<RemoveCircleOutlineOutlinedIcon />}
        />
      </div>
    </div>
  );
};
